import { useEffect, useMemo, useState } from "react";
import { api } from "./api/api";
import generateManifestListPdf from "./generateManifestListPdf";
import generateAirCargoDetailsPdf from "./generateAirCargoDetailsPdf";
import generateBolsStatusByFlightPdf from "./generateBolsStatusByFlightPdf";
import { formatNumber } from "./utils/numberFormat";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import logo from "./assets/logo.png";
import generateDeliveryOrderPdf from "./generateDeliveryOrderPdf";
import generateReceiptPdf from "./generateReceiptPdf";
import { AMIRI_BASE64 } from "./assets/fonts/Amiri-Regular.base64";

const formatDateToYMD_forReprint = (dateString) => {
  if (!dateString || typeof dateString !== 'string') return "";
  return dateString.slice(0, 10);
};

const ARABIC_RE = /[\u0600-\u06FF]/;
const SHIPMENT_CONDITION_GOOD = "Good Condition";
const SHIPMENT_CONDITION_BAD = "Bad Condition";

const normalizeShipmentCondition = (value) => {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "bad" || normalized === "bad condition") return SHIPMENT_CONDITION_BAD;
  return SHIPMENT_CONDITION_GOOD;
};

const toPdfText = (doc, value) => {
  const text = value == null ? "" : String(value);
  if (!text) return "";
  if (ARABIC_RE.test(text) && typeof doc.processArabic === "function") {
    return doc.processArabic(text);
  }
  return text;
};

// ✅ دالة توليد POD مضافة مباشرة هنا
function generatePodPdf({
  header,
  bol,
  podId,
  doReceivedDate,
  doRecipient,
  cargoReceivedDate,
  cargoRecipient,
  shipmentCondition,
}) {
  const doc = new jsPDF("p", "mm", "a4");
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 14;

  doc.addFileToVFS("Amiri-Regular.ttf", AMIRI_BASE64);
  doc.addFont("Amiri-Regular.ttf", "Amiri", "normal");
  doc.setFont("Amiri", "normal");

  // Logo
  const logoWidth = 100;
  const logoHeight = 20;
  const logoX = (pageWidth - logoWidth) / 2;
  const logoY = 5;

  doc.addImage(logo, "PNG", logoX, logoY, logoWidth, logoHeight);

  // Title
  let y = logoY + logoHeight + 15;

  doc.setFontSize(16);
  doc.text("Proof of Delivery (POD)", pageWidth / 2, y, {
    align: "center",
  });

  doc.setFontSize(10);
  y += 10;

  const addWrappedLine = (label, value, maxWidth = 180) => {
    const text = `${label}: ${value || "-"}`;
    const lines = doc.splitTextToSize(text, maxWidth);
    doc.text(lines, margin, y);
    y += lines.length * 4.5 + 1.5;
  };

  const addTwoColumns = (
    label1,
    value1,
    label2,
    value2,
    colX = 100
  ) => {
    doc.text(`${label1}:`, margin, y);
    doc.text(toPdfText(doc, value1 || "-"), margin + 24, y);
    doc.text(`${label2}:`, colX, y);
    doc.text(toPdfText(doc, value2 || "-"), colX + 26, y);
    y += 5;
  };

  // Header - بدون POD ID و Consignee
  addTwoColumns(
    "Voyage No",
    header?.voyage_number || "",
    "AWB Ref",
    bol?.bol_reference || "",
    100
  );

  y += 3;

  addTwoColumns(
    "DO Rec. Date",
    doReceivedDate,
    "DO Recipient",
    doRecipient,
    100
  );

  addTwoColumns(
    "Cargo Rec. Date",
    cargoReceivedDate,
    "Cargo Recipient",
    cargoRecipient,
    100
  );

  y += 3;

  addWrappedLine("Print Time", new Date().toLocaleString());
  addWrappedLine("Shipment Condition", shipmentCondition || "-");

  y += 5;

  // Table - مع POD ID و Consignee
  const tableBody = [
    [
      podId || "",
      bol?.bol_reference || "",
      toPdfText(doc, bol?.consignee_name || ""),
      formatNumber(bol?.number_of_packages || 0, 0),
      formatNumber(bol?.gross_mass || 0, 2),
    ],
  ];

  autoTable(doc, {
    startY: y,
    head: [["POD ID", "AWB Ref.", "Consignee", "Pkgs", "Weight"]],
    body: tableBody,
    styles: {
      font: "Amiri",
      fontSize: 8,
      cellPadding: 2,
    },
    headStyles: {
      font: "Amiri",
      fillColor: [66, 165, 245],
      textColor: 255,
      fontSize: 8,
    },
    columnStyles: {
      0: { cellWidth: 30 },
      1: { cellWidth: 40 },
      2: { cellWidth: 50 },
      3: { cellWidth: 25, halign: "center" },
      4: { cellWidth: 30, halign: "center" },
    },
    margin: {
      left: margin,
      right: margin,
    },
  });

  // Signatures
  const finalY = doc.lastAutoTable.finalY + 15;

  doc.setFontSize(11);

  doc.text("_________________________", margin, finalY);
  doc.text("Delivered By (Signature)", margin, finalY + 6);

  doc.text(
    "_________________________",
    pageWidth - margin - 50,
    finalY
  );

  doc.text(
    "Received By (Signature)",
    pageWidth - margin - 50,
    finalY + 6
  );

  // Footer
  doc.setFontSize(8);

  doc.text(
    `POD ID: ${podId} | Printed: ${new Date().toLocaleString()}`,
    margin,
    285
  );

  const fileName = `POD_${bol?.bol_reference}_${podId}.pdf`;
  doc.save(fileName);
}

function generateInvoicePdfForReprint({ header, invoice, recipientName, deliveryDate, days, discount = 0, d_no }) {
  const doc = new jsPDF("p", "mm", "a4");
  const pageWidth = doc.internal.pageSize.getWidth();
  const logoWidth = 100;
  const logoHeight = 20;
  const logoX = (pageWidth - logoWidth) / 2;
  const logoY = 5;
  doc.addImage(logo, "PNG", logoX, logoY, logoWidth, logoHeight);
  let y = logoY + logoHeight + 20;
  doc.setFontSize(16);
  doc.text("Storage Charges Invoice", pageWidth / 2, y, { align: "center" });
  doc.setFontSize(11);
  y += 20;
  doc.text(`Voyage No.: ${header.voyage_number || ""}`, 80, y);
  doc.text(`Arrival Date: ${formatDateToYMD_forReprint(header.date_of_arrival) || ""}`, 150, y);
  
  if (d_no) {
    doc.text(`Invoice No: ${d_no}`, 14, y);
  }

  y += 6;
  doc.text(`Days of Storage: ${days}`, 150, y);
  y += 6;
  const consigneeText = `Consignee / Receiver: ${recipientName || ""}`;
  const consigneeLines = doc.splitTextToSize(consigneeText, 130);
  doc.text(consigneeLines, 14, y);
  doc.text(`Delivery Date: ${formatDateToYMD_forReprint(deliveryDate) || ""}`, 150, y);

  if (consigneeLines.length > 1) {
    const lineHeight = doc.getLineHeight() / doc.internal.scaleFactor;
    y += (consigneeLines.length - 1) * lineHeight;
  }

  if (invoice.service) {
    y += 6;
    doc.text(`Service: ${invoice.service.name}`, 14, y);
    doc.text(`Rate per day: ${formatNumber(invoice.service.price, 2)}`, 150, y);
  }
  const finalAmount = invoice.total - discount;
  const tableBody = invoice.lines.map(({ bol, unitPrice, qty, weight, amount }) => [bol.bol_reference || "", bol.goods_description || "", bol.consignee_name || "", formatNumber(qty || 0, 0), formatNumber(weight || 0, 2), formatNumber(unitPrice || 0, 2), days, formatNumber(finalAmount, 2)]);
  autoTable(doc, { startY: y + 20, head: [["AWB Ref.", "Goods Description", "Consignee", "Packages", "Weight", "Rate/day", "Days", "Total Amount"]], body: tableBody, styles: { fontSize: 8 }, headStyles: { fillColor: [66, 165, 245], textColor: 255 } });
  const firstBol = invoice.lines[0]?.bol;
  const fileName = `Invoice_${header.id}_${firstBol?.bol_reference || 'unknown'}.pdf`;
  doc.save(fileName);
}

function generateIrpReportPdf({ title, operator, rows }) {
  const doc = new jsPDF("l", "mm", "a4");
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  const safeRows = Array.isArray(rows) ? rows : [];
  const generatedAt = new Date().toLocaleString();

  doc.addImage(logo, "PNG", 10, 8, 45, 10);
  doc.setFontSize(14);
  doc.text(title, pageWidth / 2, 15, { align: "center" });

  doc.setFontSize(9);
  doc.text(`Operator: ${operator || "All"}`, 10, 22);
  doc.text(`Generated: ${generatedAt}`, pageWidth - 10, 22, { align: "right" });

  const body = safeRows.map((r) => [
    `${r.flight_no || ""} / ${r.flight_date || ""}`,
    r.mawb_no || "",
    r.hawb_no || "",
    r.origin_code || "",
    formatNumber(r.pcs || 0, 0),
    formatNumber(r.wt || 0, 3),
    r.commodity || "",
    r.awb_date || "",
    r.remarks || "",
    r.delivered || "",
  ]);

  autoTable(doc, {
    startY: 27,
    head: [[
      "FLT NO / DATE",
      "MAWB NO",
      "HAWB NO",
      "ORIGIN",
      "PC(S)",
      "WT (KGS)",
      "COMMODITY",
      "DATE AWB",
      "REMARKS",
      "DELIVERED",
    ]],
    body,
    styles: { fontSize: 8, cellPadding: 1.8, valign: "middle" },
    headStyles: { fillColor: [40, 80, 150], textColor: 255, fontSize: 8.5 },
    columnStyles: {
      0: { cellWidth: 34 },
      1: { cellWidth: 25 },
      2: { cellWidth: 25 },
      3: { cellWidth: 20 },
      4: { cellWidth: 14, halign: "center" },
      5: { cellWidth: 18, halign: "center" },
      6: { cellWidth: 58 },
      7: { cellWidth: 23, halign: "center" },
      8: { cellWidth: 42 },
      9: { cellWidth: 24, halign: "center" },
    },
    margin: { top: 27, left: 8, right: 8, bottom: 16 },
    didDrawPage: (data) => {
      const awbFooter = safeRows.slice(0, 8).map((x) => x.bol_reference).filter(Boolean).join(", ");
      const awbText = awbFooter ? `AWB: ${awbFooter}${safeRows.length > 8 ? " ..." : ""}` : "AWB: -";

      doc.setFontSize(8);
      doc.text(awbText, 8, pageHeight - 8);
      doc.text(
        `Records: ${safeRows.length} | Page ${doc.internal.getNumberOfPages()}`,
        pageWidth - 8,
        pageHeight - 8,
        { align: "right" }
      );
    },
  });

  const fileSafeTitle = title.replace(/\s+/g, "_");
  doc.save(`${fileSafeTitle}.pdf`);
}

function readPerms() {
  try {
    return JSON.parse(localStorage.getItem("permissions") || "[]");
  } catch {
    return [];
  }
}

function has(perms, code) {
  return Array.isArray(perms) && perms.includes(code);
}

function isDirectServiceItem(item) {
  const values = [
    item?.service?.name,
    item?.service?.code,
    item?.bol?.service_name,
    item?.bol?.service,
    item?.bol?.service_type,
    item?.bol?.service_code,
  ];

  return values.some((v) => String(v || "").trim().toLowerCase() === "direct");
}

export default function ReportsScreen({ permissions: permsProp }) {
  // ✅ Hooks أولاً (بدون أي return قبلها)
  const permissions = permsProp || readPerms();
  const canView = useMemo(() => has(permissions, "USERS_ADMIN"), [permissions]);

  // Tabs
  const [tab, setTab] = useState("manifests");

  // Shared Filters
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // Flights
  const [flights, setFlights] = useState([]);
  const [flightQuery, setFlightQuery] = useState("");
  const [flightNo, setFlightNo] = useState("");

  // Manifest
  const [manifestId, setManifestId] = useState("");
  const [manifests, setManifests] = useState([]);

  // Stock
  const [stockQuery, setStockQuery] = useState("");
  const [stockItems, setStockItems] = useState([]);
  const [stockSummary, setStockSummary] = useState(null);
  const [stockMoves, setStockMoves] = useState([]);
  const [manifestFilterId, setManifestFilterId] = useState("");
  const [stockByService, setStockByService] = useState(null);
  const [manifestDeclared, setManifestDeclared] = useState([]);
  const [manifestDeclaredVisible, setManifestDeclaredVisible] = useState(false);

  // Financial
  const [financialReport, setFinancialReport] = useState(null);
  const [deliveredReport, setDeliveredReport] = useState(null);
  const [deliveredDetailed, setDeliveredDetailed] = useState(null);

  // IRP
  const [irpOperators, setIrpOperators] = useState([]);
  const [irpOperator, setIrpOperator] = useState("");
  const [irpUndelivered, setIrpUndelivered] = useState([]);
  const [irpDelivered, setIrpDelivered] = useState([]);

  // UI state
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");

  // Reprint state
  const [reprintQuery, setReprintQuery] = useState("");
  const [reprintResult, setReprintResult] = useState(null);
  const [doModalData, setDoModalData] = useState(null);
  const [doModalForm, setDoModalForm] = useState({
    doRecipient: "",
    doRecipientPhone: "",
    doReceivedDate: "",
  });
  const [doModalSaving, setDoModalSaving] = useState(false);

  const [podModalData, setPodModalData] = useState(null);
  const [podModalForm, setPodModalForm] = useState({
    doReceivedDate: "",
    doRecipient: "",
    doRecipientPhone: "",
    cargoReceivedDate: "",
    cargoRecipient: "",
    shipmentCondition: SHIPMENT_CONDITION_GOOD,
  });
  const [podModalSaving, setPodModalSaving] = useState(false);
  const [podReprintShipmentCondition, setPodReprintShipmentCondition] = useState(SHIPMENT_CONDITION_GOOD);
  const [podReprintModalData, setPodReprintModalData] = useState(null);
  const [podReprintModalCondition, setPodReprintModalCondition] = useState(SHIPMENT_CONDITION_GOOD);

    // ✅ POD Reprint state
  const [podList, setPodList] = useState([]);
  const [selectedPodId, setSelectedPodId] = useState("");
  const [podSearchQuery, setPodSearchQuery] = useState("");
  const [podDetails, setPodDetails] = useState(null);

  // Split stock movements into IN and OUT
  const inMoves = useMemo(() => stockMoves.filter((m) => m.move_type === "IN"), [stockMoves]);
  const outMoves = useMemo(() => stockMoves.filter((m) => m.move_type === "OUT"), [stockMoves]);

  // Hide manifest report automatically when switching to Warehouse tab
  useEffect(() => {
    if (tab === "stock") setManifestDeclaredVisible(false);
  }, [tab]);

    // Filtered POD list
  const filteredPodList = useMemo(() => {
    const list = Array.isArray(podList) ? podList : [];
    const q = podSearchQuery.trim().toLowerCase();
    if (!q) return list;
    return list.filter((p) =>
      String(p.id || "").includes(q) ||
      String(p.bol_reference || "").toLowerCase().includes(q) ||
      String(p.consignee_name || "").toLowerCase().includes(q)
    );
  }, [podList, podSearchQuery]);

  useEffect(() => {
    if (tab === "stock") setManifestDeclaredVisible(false);
  }, [tab]);

   // ✅ جلب قائمة POD IDs عند فتح تبويب POD Reprint
  useEffect(() => {
    if (tab !== "pod-reprint") return;
    
    const fetchPodList = async () => {
      try {
        setLoading(true);
        const res = await api.get("/pods/list");
        const list = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];
        setPodList(list);
      } catch (err) {
        console.error("Error fetching POD list:", err);
        setPodList([]);
        setMsg("Failed to load POD list");
      } finally {
        setLoading(false);
      }
    };
    
    fetchPodList();
  }, [tab]);


  // ✅ جلب قائمة المنافيست
  useEffect(() => {
    if (!canView) return;
    (async () => {
      try {
        const res = await api.get("/manifests/list");
        const list = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];
        setManifests(list);
      } catch {
        setManifests([]);
      }
    })();
  }, [canView]);

  // ✅ جلب أرقام الرحلات
  useEffect(() => {
    if (!canView) return;
    (async () => {
      try {
        const res = await api.get(`/reports/flights${qs({ from: dateFrom, to: dateTo })}`);
        const list = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];
        const normalized = list.map((x) => (typeof x === "string" ? x : x?.flight_no || x?.flightNo || "")).filter(Boolean);
        setFlights(normalized);
      } catch {
        setFlights([]);
      }
    })();
  }, [canView, dateFrom, dateTo]);

  useEffect(() => {
    if (!canView) return;
    (async () => {
      try {
        const res = await api.get("/reports/operators");
        setIrpOperators(Array.isArray(res) ? res : []);
      } catch {
        setIrpOperators([]);
      }
    })();
  }, [canView]);

  // ✅ بعد كل الـ Hooks نعمل شرط الصلاحية
  if (!canView) {
    return (
      <div className="card">
        <h3>Reports</h3>
        <div className="message error">you don't have permission</div>
      </div>
    );
  }

  const filteredFlights = (Array.isArray(flights) ? flights : []).filter((f) =>
    String(f).toLowerCase().includes(String(flightQuery || "").toLowerCase())
  );

  const qs = (obj) => {
    const p = new URLSearchParams();
    Object.entries(obj || {}).forEach(([k, v]) => {
      if (v != null && String(v).trim() !== "") p.set(k, String(v));
    });
    const s = p.toString();
    return s ? `?${s}` : "";
  };

  // ===== Manifest exports =====
  const exportManifestList = async () => {
    try {
      setLoading(true);
      setMsg("");
      const res = await api.get(`/reports/manifest-list${qs({ from: dateFrom, to: dateTo, flightNo: flightNo })}`);
      generateManifestListPdf(res || [], { from: dateFrom, to: dateTo });
      setMsg("generated Manifest List ✅");
    } catch (e) {
      console.error(e);
      setMsg(e?.message || "failed to generate report");
    } finally {
      setLoading(false);
    }
  };

  const loadManifestDeclaredVsActual = async () => {
    try {
      setLoading(true);
      setMsg("");
      const res = await api.get(`/reports/manifest-declared-vs-actual${qs({ from: dateFrom, to: dateTo, flightNo: flightNo })}`);
      setManifestDeclared(Array.isArray(res) ? res : []);
      setMsg("loaded Manifest Declared vs Actual ✅");
    } catch (e) {
      console.error(e);
      setManifestDeclared([]);
      setMsg(e?.message || "failed to load report");
    } finally {
      setLoading(false);
    }
  };

  const exportAirCargoByFlight = async () => {
    const f = String(flightNo || "").trim();
    if (!f) {
      setMsg("Please select a valid Flight No");
      return;
    }
    try {
      setLoading(true);
      setMsg("");
      const res = await api.get(`/reports/air-cargo-by-flight${qs({ flightNo: f, from: dateFrom, to: dateTo })}`);
      generateAirCargoDetailsPdf(res);
      setMsg("generated Air Cargo report by flight ✅");
    } catch (e) {
      console.error(e);
      setMsg(e?.message || "failed to generate report");
    } finally {
      setLoading(false);
    }
  };

  const exportBolsStatusByFlight = async () => {
    const f = String(flightNo || "").trim();
    if (!f) {
      setMsg("Please select a valid Flight No");
      return;
    }
    try {
      setLoading(true);
      setMsg("");
      const res = await api.get(`/reports/bols-status-by-flight${qs({ flightNo: f, from: dateFrom, to: dateTo })}`);
      generateBolsStatusByFlightPdf(res);
      setMsg("BOL Status report generated ✅");
    } catch (e) {
      console.error(e);
      setMsg(e?.message || "Failed to generate report");
    } finally {
      setLoading(false);
    }
  };

  const loadFinancialReport = async () => {
    try {
      setLoading(true);
      setMsg("");
      const res = await api.get(`/reports/stock-value-by-operator${qs({ fromDate: dateFrom, toDate: dateTo })}`);
      setFinancialReport(res);
      setMsg("Financial report loaded successfully ✅");
    } catch (e) {
      console.error(e);
      setFinancialReport(null);
      setMsg(e?.message || "Failed to load financial report");
    } finally {
      setLoading(false);
    }
  };

  const loadDeliveredReport = async () => {
    try {
      setLoading(true);
      setMsg("");
      const res = await api.get(`/reports/delivered-stock-by-operator${qs({ fromDate: dateFrom, toDate: dateTo })}`);
      setDeliveredReport(res);
      setMsg("Delivered report loaded successfully ✅");
    } catch (e) {
      console.error(e);
      setDeliveredReport(null);
      setMsg(e?.message || "Failed to load delivered report");
    } finally {
      setLoading(false);
    }
  };

  const loadDeliveredDetailed = async () => {
    try {
      setLoading(true);
      setMsg("");
      const res = await api.get(`/reports/delivered-stock-detailed${qs({ from: dateFrom, to: dateTo })}`);
      setDeliveredDetailed(Array.isArray(res) ? res : []);
      setMsg("Detailed Delivered report loaded successfully ✅");
    } catch (e) {
      console.error(e);
      setDeliveredDetailed(null);
      setMsg(e?.message || "Failed to load detailed delivered report");
    } finally {
      setLoading(false);
    }
  };

  const loadIrpUndelivered = async () => {
    try {
      setLoading(true);
      setMsg("");
      const res = await api.get(`/reports/irp-shipments${qs({ status: "undelivered", operator: irpOperator, from: dateFrom, to: dateTo })}`);
      setIrpUndelivered(Array.isArray(res?.rows) ? res.rows : []);
      setMsg("Undelivered IRP report loaded successfully ✅");
    } catch (e) {
      console.error(e);
      setIrpUndelivered([]);
      setMsg(e?.message || "Failed to load Undelivered IRP report");
    } finally {
      setLoading(false);
    }
  };

  const loadIrpDelivered = async () => {
    try {
      setLoading(true);
      setMsg("");
      const res = await api.get(`/reports/irp-shipments${qs({ status: "delivered", operator: irpOperator, from: dateFrom, to: dateTo })}`);
      setIrpDelivered(Array.isArray(res?.rows) ? res.rows : []);
      setMsg("Delivered IRP report loaded successfully ✅");
    } catch (e) {
      console.error(e);
      setIrpDelivered([]);
      setMsg(e?.message || "Failed to load Delivered IRP report");
    } finally {
      setLoading(false);
    }
  };

  // ===== Stock loaders =====
  const loadStock = async () => {
    try {
      setLoading(true);
      setMsg("");
      const [items, summary, moves] = await Promise.all([
        api.get(`/reports/stock${qs({ q: stockQuery })}`),
        api.get(`/reports/stock-summary${qs({ from: dateFrom, to: dateTo })}`),
        api.get(`/reports/stock-movements${qs({ from: dateFrom, to: dateTo, ref: stockQuery || undefined })}`),
      ]);
      setStockItems(Array.isArray(items) ? items : []);
      setStockSummary(summary || null);
      setStockMoves(Array.isArray(moves) ? moves : []);
      setMsg("updated Warehouse report ✅");
    } catch (e) {
      console.error(e);
      setMsg(e?.message || "failed to fetch Warehouse report");
    } finally {
      setLoading(false);
    }
  };

  const loadStockByService = async () => {
    try {
      setLoading(true);
      setMsg("");
      const res = await api.get(`/reports/stock-by-service${qs({ q: stockQuery })}`);
      setStockByService(res || null);
      setMsg("updated Stock By Service report ✅");
    } catch (e) {
      console.error(e);
      setStockByService(null);
      setMsg(e?.message || "failed to fetch Stock By Service report");
    } finally {
      setLoading(false);
    }
  };

  const loadStockSummary = async () => {
    try {
      setLoading(true);
      setMsg("");
      const s = await api.get(`/reports/stock-summary${qs({ from: dateFrom, to: dateTo })}`);
      setStockSummary(s || null);
    } catch (e) {
      console.error(e);
      setMsg(e?.message || "failed to fetch stock summary");
      setStockSummary(null);
    } finally {
      setLoading(false);
    }
  };

  const loadStockMoves = async () => {
    try {
      setLoading(true);
      setMsg("");
      const rows = await api.get(`/reports/stock-movements${qs({ from: dateFrom, to: dateTo, ref: stockQuery, manifestId: manifestFilterId })}`);
      setStockMoves(Array.isArray(rows) ? rows : []);
    } catch (e) {
      console.error(e);
      setMsg(e?.message || "failed to fetch stock movements");
      setStockMoves([]);
    } finally {
      setLoading(false);
    }
  };

  // ===== Reprint handlers =====
  const handleReprintSearch = async () => {
    if (!reprintQuery.trim()) return;
    setLoading(true);
    setMsg("");
    setReprintResult(null);
    try {
      const res = await api.get(`/bols/for-reprint/${encodeURIComponent(reprintQuery.trim())}`);
      setReprintResult(res);
      setMsg("AWB data loaded successfully.");
    } catch (e) {
      console.error(e);
      setReprintResult(null);
      setMsg(e?.message || "Failed to find AWB.");
    } finally {
      setLoading(false);
    }
  };

  const getTodayYmd = () => new Date().toISOString().slice(0, 10);

  const updateReprintBolLocal = (bolId, patch) => {
    setReprintResult((prev) => {
      if (!Array.isArray(prev)) return prev;
      return prev.map((item) => {
        if (!item?.bol || Number(item.bol.id) !== Number(bolId)) return item;
        return { ...item, bol: { ...item.bol, ...patch } };
      });
    });
  };

  const openDoMetadataModal = (data) => {
    const bol = data?.bol || {};
    setDoModalData(data);
    setDoModalForm(getDoMetaDefaults(bol));
  };

  const closeDoMetadataModal = () => {
    setDoModalData(null);
    setDoModalForm({
      doRecipient: "",
      doRecipientPhone: "",
      doReceivedDate: "",
    });
  };

  const isDoMetaMissing = (bol) => {
    if (!bol) return true;
    const doRecipient = String(bol?.do_recipient ?? bol?.doRecipient ?? "").trim();
    const doRecipientPhone = String(bol?.do_recipient_phone ?? bol?.doRecipientPhone ?? "").trim();
    const doReceivedDate = String(bol?.do_received_date ?? bol?.doReceivedDate ?? "").trim();
    return !doRecipient || !doRecipientPhone || !doReceivedDate;
  };

  const getDoMetaDefaults = (bol) => {
    const doRecipient = String(bol?.do_recipient ?? bol?.doRecipient ?? "").trim();
    const doRecipientPhone = String(bol?.do_recipient_phone ?? bol?.doRecipientPhone ?? "").trim();
    const doReceivedDate = String(bol?.do_received_date ?? bol?.doReceivedDate ?? "").trim();
    const fallbackDate = formatDateToYMD_forReprint(doReceivedDate) || getTodayYmd();
    return {
      doRecipient,
      doRecipientPhone,
      doReceivedDate: fallbackDate,
    };
  };

  const handleSaveDoMetadataAndPrint = async () => {
    if (!doModalData?.bol || !doModalData?.header) {
      setMsg("Cannot print, data is missing.");
      return;
    }

    const doRecipient = String(doModalForm.doRecipient || "").trim();
    const doRecipientPhone = String(doModalForm.doRecipientPhone || "").trim();
    const doReceivedDate = String(doModalForm.doReceivedDate || "").trim();

    if (!doRecipient || !doRecipientPhone || !doReceivedDate) {
      setMsg("Please fill DO Recipient, Phone and Received Date.");
      return;
    }

    try {
      setDoModalSaving(true);
      await api.put(`/bols/${doModalData.bol.id}/do-metadata`, {
        doRecipient,
        doRecipientPhone,
        doReceivedDate,
      });

      const updatedBol = {
        ...doModalData.bol,
        do_recipient: doRecipient,
        do_recipient_phone: doRecipientPhone,
        do_received_date: doReceivedDate,
      };

      updateReprintBolLocal(doModalData.bol.id, {
        do_recipient: doRecipient,
        do_recipient_phone: doRecipientPhone,
        do_received_date: doReceivedDate,
      });

      generateDeliveryOrderPdf({ bol: updatedBol, header: doModalData.header });
      generateReceiptPdf({ bol: updatedBol });
      closeDoMetadataModal();
      setMsg("DO and Receipt re-printed successfully.");
    } catch (e) {
      console.error(e);
      setMsg(e?.message || "Failed to update DO data.");
    } finally {
      setDoModalSaving(false);
    }
  };

  const handleReprintDO = (data) => {
    if (!data || !data.bol || !data.header) {
      setMsg("Cannot print, data is missing.");
      return;
    }

    const bol = data.bol;
    const bolWithMeta = {
      ...bol,
      do_recipient: bol.do_recipient ?? bol.doRecipient ?? "",
      do_recipient_phone: bol.do_recipient_phone ?? bol.doRecipientPhone ?? "",
      do_received_date: bol.do_received_date ?? bol.doReceivedDate ?? getTodayYmd(),
    };

    if (isDoMetaMissing(bolWithMeta)) {
      openDoMetadataModal({ ...data, bol: bolWithMeta });
      return;
    }

    generateDeliveryOrderPdf({ bol: bolWithMeta, header: data.header });
    generateReceiptPdf({ bol: bolWithMeta });
    setMsg("DO and Receipt re-printed successfully.");
  };

  const handleReprintInvoice = (data) => {
    if (!data || !data.bol || !data.header || !data.service) {
      setMsg("Cannot print invoice, data is missing (BOL, Header, or Service).");
      return;
    }
    const { bol, header, service } = data;

    const arrivalDate = new Date(header.date_of_arrival);
    const deliveryD = new Date(bol.delivery_date);

    if (isNaN(arrivalDate.getTime()) || isNaN(deliveryD.getTime())) {
      setMsg("Invalid arrival or delivery date.");
      return;
    }

    const diffMs = deliveryD.getTime() - arrivalDate.getTime();
    let days = Math.round(diffMs / (1000 * 60 * 60 * 24)) + 1;
    if (bol.day === 1) {
      days -= 1;
    }
    days = Math.max(1, days);

    const totalBeforeDiscount = Number(bol.invoice_amount || 0);
    const discountAmount = Number(bol.discount || 0);

    const invoice = {
      service: service,
      total: totalBeforeDiscount,
      lines: [{ bol: bol, unitPrice: service.price, qty: bol.number_of_packages, weight: bol.gross_mass, amount: totalBeforeDiscount }],
    };

    generateInvoicePdfForReprint({
      header: header,
      invoice: invoice,
      recipientName: bol.delivered_to,
      deliveryDate: bol.delivery_date,
      days: days,
      discount: discountAmount,
      d_no: bol.d_no,
    });
    setMsg("Invoice re-printed successfully.");
  };

  const openPodModal = (item) => {
    const bol = item?.bol || {};
    const defaultDoReceivedDate = formatDateToYMD_forReprint(bol.do_received_date) || getTodayYmd();
    setPodModalData(item);
    setPodModalForm({
      doReceivedDate: defaultDoReceivedDate,
      doRecipient: bol.do_recipient || "",
      doRecipientPhone: bol.do_recipient_phone || "",
      cargoReceivedDate: formatDateToYMD_forReprint(bol.delivery_date) || getTodayYmd(),
      cargoRecipient: bol.delivered_to || bol.consignee_name || "",
      shipmentCondition: SHIPMENT_CONDITION_GOOD,
    });
  };

  const closePodModal = () => {
    setPodModalData(null);
    setPodModalForm({
      doReceivedDate: "",
      doRecipient: "",
      doRecipientPhone: "",
      cargoReceivedDate: "",
      cargoRecipient: "",
      shipmentCondition: SHIPMENT_CONDITION_GOOD,
    });
  };

  const handleCreatePodAndPrint = async () => {
    if (!podModalData?.bol) {
      setMsg("Cannot create POD, data is missing.");
      return;
    }

    const doReceivedDate = String(podModalForm.doReceivedDate || "").trim();
    const doRecipient = String(podModalForm.doRecipient || "").trim();
    const doRecipientPhone = String(podModalForm.doRecipientPhone || "").trim();
    const cargoReceivedDate = String(podModalForm.cargoReceivedDate || "").trim();
    const cargoRecipient = String(podModalForm.cargoRecipient || "").trim();
    const shipmentCondition = String(podModalForm.shipmentCondition || "").trim();

    if (!doReceivedDate || !doRecipient) {
      setMsg("DO Received Date and DO Recipient are required.");
      return;
    }

    try {
      setPodModalSaving(true);
      const createRes = await api.post(`/bols/${podModalData.bol.id}/pod`, {
        doReceivedDate,
        doRecipient,
        doRecipientPhone,
        cargoReceivedDate: cargoReceivedDate || null,
        cargoRecipient: cargoRecipient || null,
      });

      const podId = createRes?.id || createRes?.data?.id;

      // تحديث محلي لبيانات البوليصة حتى تنعكس مباشرة في إعادة طباعة DO
      updateReprintBolLocal(podModalData.bol.id, {
        do_received_date: doReceivedDate,
        do_recipient: doRecipient,
        do_recipient_phone: doRecipientPhone,
      });

      const detailsRes = await api.get(`/bols/${podModalData.bol.id}/details`);
      const details = detailsRes?.data || detailsRes;

      if (!details?.bol || !details?.header) {
        setMsg("POD created but failed to load details for print.");
        closePodModal();
        return;
      }

      generatePodPdf({
        header: details.header,
        bol: details.bol,
        podId: podId || "",
        doReceivedDate,
        doRecipient,
        cargoReceivedDate,
        cargoRecipient,
        shipmentCondition,
      });

      closePodModal();
      setMsg(`POD created and printed successfully ✅${podId ? ` (ID: ${podId})` : ""}`);
    } catch (err) {
      console.error("Create POD error:", err);
      setMsg(err?.message || "Failed to create and print POD.");
    } finally {
      setPodModalSaving(false);
    }
  };

  // ✅ دالة إعادة طباعة POD
  const handleReprintPOD = async (item, shipmentConditionInput = SHIPMENT_CONDITION_GOOD) => {
    const bolId = item?.bol?.id;
    if (!bolId) {
      setMsg("Cannot re-print POD, BOL id is missing.");
      return;
    }

    const shipmentCondition = normalizeShipmentCondition(shipmentConditionInput);

    try {
      setLoading(true);
      setMsg("");
      
      // جلب POD الموجود
      const podRes = await api.get(`/bols/${bolId}/pod`);
      const podData = podRes?.data || podRes;
      
      if (!podData || !podData.id) {
        setMsg("No POD found for this AWB.");
        return;
      }

      // جلب تفاصيل البوليصة والهيدر
      const detailsRes = await api.get(`/bols/${bolId}/details`);
      const details = detailsRes?.data || detailsRes;

      if (!details || !details.bol || !details.header) {
        setMsg("Cannot re-print POD, missing BOL or Header data.");
        return;
      }

      generatePodPdf({
        header: details.header,
        bol: details.bol,
        podId: podData.id,
        doReceivedDate: podData.do_received_date,
        doRecipient: podData.do_recipient,
        cargoReceivedDate: podData.cargo_received_date,
        cargoRecipient: podData.cargo_recipient,
        shipmentCondition: String(shipmentCondition).trim(),
      });

      setMsg(`POD re-printed successfully ✅ (ID: ${podData.id})`);
    } catch (err) {
      console.error("Reprint POD error:", err);
      if (err.status === 404 || (err.message && err.message.includes("404"))) {
        openPodModal(item);
      } else {
        setMsg(err?.message || "Failed to re-print POD.");
      }
    } finally {
      setLoading(false);
    }
  };

  // ✅ POD Reprint by ID handlers
  const handleLoadPodDetails = async () => {
    if (!selectedPodId) {
      setMsg("Please select a POD ID");
      return;
    }
    
    try {
      setLoading(true);
      setMsg("");
      setPodDetails(null);
      
      const res = await api.get(`/pod/${selectedPodId}/full`);
      const data = res?.data || res;
      
      if (!data || !data.id) {
        setMsg("POD not found");
        return;
      }
      
      setPodDetails(data);
      setMsg(`POD #${data.id} loaded successfully`);
    } catch (err) {
      console.error("Error loading POD:", err);
      setMsg(err?.message || "Failed to load POD details");
      setPodDetails(null);
    } finally {
      setLoading(false);
    }
  };

  const handlePrintPodById = () => {
    if (!podDetails) {
      setMsg("Please load POD details first");
      return;
    }

    generatePodPdf({
      header: {
        voyage_number: podDetails.voyage_number,
        date_of_arrival: podDetails.date_of_arrival,
        place_of_departure_code: podDetails.place_of_departure_code,
        place_of_destination_code: podDetails.place_of_destination_code,
      },
      bol: {
        bol_reference: podDetails.bol_reference,
        consignee_name: podDetails.bol_consignee || podDetails.consignee_name,
        number_of_packages: podDetails.number_of_packages,
        gross_mass: podDetails.gross_mass,
        goods_description: podDetails.goods_description,
      },
      podId: podDetails.id,
      doReceivedDate: podDetails.do_received_date,
      doRecipient: podDetails.do_recipient,
      cargoReceivedDate: podDetails.cargo_received_date,
      cargoRecipient: podDetails.cargo_recipient,
      shipmentCondition: String(podReprintShipmentCondition || "").trim(),
    });

    setMsg(`POD #${podDetails.id} printed successfully ✅`);
  };

  return (
    <div className="screen screen-rtl">
      <div className="screen-header" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <h2 style={{ margin: 0 }}>Reports</h2>

        {/* Tabs */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button
            type="button"
            onClick={() => setTab("manifests")}
            style={{
              backgroundColor: tab === "manifests" ? "#1e88e5" : "#f5f5f5",
              color: tab === "manifests" ? "#fff" : "#333",
              border: "1px solid #1e88e5",
              padding: "8px 14px",
              borderRadius: 6,
              cursor: "pointer",
              fontWeight: 600,
            }}
          >
            Manifest Reports
          </button>

          <button
            type="button"
            onClick={() => {
              setManifestDeclaredVisible(false);
              setTab("stock");
            }}
            style={{
              backgroundColor: tab === "stock" ? "#1e88e5" : "#f5f5f5",
              color: tab === "stock" ? "#fff" : "#333",
              border: "1px solid #1e88e5",
              padding: "8px 14px",
              borderRadius: 6,
              cursor: "pointer",
              fontWeight: 600,
            }}
          >
            Warehouse Reports
          </button>

          <button
            type="button"
            onClick={() => setTab("financial")}
            style={{
              backgroundColor: tab === "financial" ? "#1e88e5" : "#f5f5f5",
              color: tab === "financial" ? "#fff" : "#333",
              border: "1px solid #1e88e5",
              padding: "8px 14px",
              borderRadius: 6,
              cursor: "pointer",
              fontWeight: 600,
            }}
          >
            Financial Reports
          </button>

          <button
            type="button"
            onClick={() => setTab("delivered")}
            style={{
              backgroundColor: tab === "delivered" ? "#1e88e5" : "#f5f5f5",
              color: tab === "delivered" ? "#fff" : "#333",
              border: "1px solid #1e88e5",
              padding: "8px 14px",
              borderRadius: 6,
              cursor: "pointer",
              fontWeight: 600,
            }}
          >
            Delivered Reports
          </button>

          <button
            type="button"
            onClick={() => setTab("reprint")}
            style={{
              backgroundColor: tab === "reprint" ? "#1e88e5" : "#f5f5f5",
              color: tab === "reprint" ? "#fff" : "#333",
              border: "1px solid #1e88e5",
              padding: "8px 14px",
              borderRadius: 6,
              cursor: "pointer",
              fontWeight: 600,
            }}
          >
            Re-print
          </button>

          <button
            type="button"
            onClick={() => setTab("irp")}
            style={{
              backgroundColor: tab === "irp" ? "#1e88e5" : "#f5f5f5",
              color: tab === "irp" ? "#fff" : "#333",
              border: "1px solid #1e88e5",
              padding: "8px 14px",
              borderRadius: 6,
              cursor: "pointer",
              fontWeight: 600,
            }}
          >
            IRP Report
          </button>

          {/* <button
            type="button"
            onClick={() => setTab("pod-reprint")}
            style={{
              backgroundColor: tab === "pod-reprint" ? "#1e88e5" : "#f5f5f5",
              color: tab === "pod-reprint" ? "#fff" : "#333",
              border: "1px solid #1e88e5",
              padding: "8px 14px",
              borderRadius: 6,
              cursor: "pointer",
              fontWeight: 600,
            }}
          >
            POD Reprint
          </button> */}
        </div>
      </div>

      {/* Shared filters */}
      <div className="card">
        <h3>Filters</h3>
        <div className="grid grid-auto">
          <div className="field">
            <label>From</label>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          </div>
          <div className="field">
            <label>To</label>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </div>

          {tab === "manifests" && (
            <div className="field">
              <label>Flight No (Air Cargo Report)</label>
              <input
                value={flightNo}
                onChange={(e) => setFlightNo(e.target.value)}
                placeholder="مثال: 8V601"
                list="flightNos"
              />
              <datalist id="flightNos">
                {(Array.isArray(flights) ? flights : []).slice(0, 500).map((f) => (
                  <option key={f} value={f}>{f}</option>
                ))}
              </datalist>
            </div>
          )}

          {tab === "stock" && (
            <div className="field">
              <label>Filter by AWB</label>
              <input
                value={stockQuery}
                onChange={(e) => setStockQuery(e.target.value)}
                placeholder="example: 2569"
              />
            </div>
          )}

          {tab === "reprint" && (
            <div className="field">
              <label>Search by AWB to Re-print</label>
              <input
                value={reprintQuery}
                onChange={(e) => setReprintQuery(e.target.value)}
                placeholder="Enter AWB reference..."
              />
            </div>
          )}

          {tab === "irp" && (
            <div className="field">
              <label>Operator</label>
              <select value={irpOperator} onChange={(e) => setIrpOperator(e.target.value)}>
                <option value="">All Operators</option>
                {irpOperators.map((op) => (
                  <option key={op} value={op}>{op}</option>
                ))}
              </select>
            </div>
          )}

          {/* ✅ فلتر POD Reprint */}
          {tab === "pod-reprint" && (
            <div className="field">
              <label>Search POD (ID / AWB / Consignee)</label>
              <input
                value={podSearchQuery}
                onChange={(e) => setPodSearchQuery(e.target.value)}
                placeholder="Type to filter POD list..."
              />
            </div>
          )}
        </div>

        {msg && (
          <div className={msg.includes("failed") || msg.includes("Error") || msg.includes("missing") || msg.includes("No POD") || msg.includes("not found") ? "message error" : "message success"} style={{ marginTop: 10 }}>
            {msg}


          </div>
        )}
      </div>
       

      {/* ===== Tab: Manifests ===== */}
      {tab === "manifests" && (
        <div className="card">
          <h3>Manifest Reports (Export PDF)</h3>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button type="button" onClick={exportManifestList} disabled={loading}>
              {loading ? "..." : "Print/Export PDF - Manifest List"}
            </button>
            <button type="button" onClick={exportAirCargoByFlight} disabled={loading}>
              {loading ? "..." : "Print/Export PDF - Air Cargo By Flight"}
            </button>
            <button type="button" onClick={exportBolsStatusByFlight} disabled={loading}>
              {loading ? "..." : "Export PDF - BOL Status By Flight"}
            </button>
            <button
              type="button"
              onClick={async () => {
                if (!manifestDeclaredVisible) {
                  await loadManifestDeclaredVsActual();
                  setManifestDeclaredVisible(true);
                } else {
                  setManifestDeclaredVisible(false);
                }
              }}
              disabled={loading}
            >
              {loading ? "..." : manifestDeclaredVisible ? "Hide Manifest Declared vs Actual" : "Show Manifest Declared vs Actual"}
            </button>
          </div>
        </div>
      )}
      
      {manifestDeclaredVisible && Array.isArray(manifestDeclared) && manifestDeclared.length > 0 && (
        <div className="card" style={{ marginTop: 12 }}>
          <h3>Manifest Declared vs Actual</h3>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Manifest ID</th>
                  <th>BOLs</th>
                  <th>Arrival</th>
                  <th>Declared</th>
                  <th>Actual</th>
                  <th>Remaining</th>
                </tr>
              </thead>
              <tbody>
                {manifestDeclared.map((m) => {
                  const declared = m.declared_total_bols == null ? "" : m.declared_total_bols;
                  const actual = m.actual_total_bols || 0;
                  const isMatch = Number(declared) === Number(actual);
                  const isFullyDelivered = Number(m.remaining_bols || 0) === 0;
                  const bg = isFullyDelivered && isMatch ? "#dff0d8" : !isMatch ? "#f8d7da" : "transparent";
                  return (
                    <tr key={m.id} style={{ backgroundColor: bg }}>
                      <td>{m.id}</td>
                      <td>
                        {Array.isArray(m.bols) && m.bols.length > 0 ? (
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                            {m.bols.map((b) => (
                              <span
                                key={b.id || `${m.id}-${b.bol_reference}`}
                                style={{
                                  display: "inline-block",
                                  padding: "2px 8px",
                                  borderRadius: 999,
                                  fontSize: 12,
                                  backgroundColor: Number(b.is_delivered || 0) === 1 ? "#e8f5e9" : "#fff3e0",
                                  border: `1px solid ${Number(b.is_delivered || 0) === 1 ? "#43a047" : "#fb8c00"}`,
                                }}
                                title={Number(b.is_delivered || 0) === 1 ? "Delivered" : "Pending"}
                              >
                                {b.bol_reference || "-"}
                              </span>
                            ))}
                          </div>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td>{m.date_of_arrival ? String(m.date_of_arrival).slice(0, 10) : ""}</td>
                      <td>{declared}</td>
                      <td>{actual}</td>
                      <td>{m.remaining_bols}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ===== Tab: Stock ===== */}
      {tab === "stock" && (
        <div className="card">
          <h3>Warehouse Reports</h3>

          {/* Section 1: Current Stock */}
          <div className="card" style={{ marginTop: 12 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
              <h4 style={{ margin: 0 }}>Current Stock</h4>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <button type="button" onClick={loadStock} disabled={loading}>
                  {loading ? "..." : "Load Current Stock"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setStockItems([]);
                    setMsg("Current Stock cleared ✅");
                  }}
                  style={{
                    padding: "6px 10px",
                    fontSize: 12,
                    borderRadius: 6,
                    border: "1px solid #bbb",
                    background: "#f5f5f5",
                    cursor: "pointer",
                  }}
                >
                  Clear
                </button>
              </div>
            </div>

            {Array.isArray(stockItems) && stockItems.length > 0 && (
              <div className="table-wrap" style={{ marginTop: 12 }}>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>AWB</th>
                      <th>Current</th>
                      <th>Added</th>
                      <th>Withdrawn</th>
                      <th>Updated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stockItems.map((r) => (
                      <tr key={r.bol_reference}>
                        <td>{r.bol_reference}</td>
                        <td>{formatNumber(r.current_qty, 0)}</td>
                        <td>{formatNumber(r.total_in, 0)}</td>
                        <td>{formatNumber(r.total_out, 0)}</td>
                        <td>{r.updated_at ? String(r.updated_at).slice(0, 19).replace("T", " ") : ""}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Section 2: Stock Summary */}
          <div className="card" style={{ marginTop: 12 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
              <h4 style={{ margin: 0 }}>Stock Summary</h4>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <button type="button" onClick={loadStockSummary} disabled={loading}>
                  {loading ? "..." : "Load Summary"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setStockSummary(null);
                    setMsg("Summary cleared ✅");
                  }}
                  style={{
                    padding: "6px 10px",
                    fontSize: 12,
                    borderRadius: 6,
                    border: "1px solid #bbb",
                    background: "#f5f5f5",
                    cursor: "pointer",
                  }}
                >
                  Clear
                </button>
              </div>
            </div>

            <div style={{ marginTop: 12, display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button type="button" onClick={loadStockByService} disabled={loading}>
                {loading ? "..." : "Load Stock By Service"}
              </button>
              <button
                type="button"
                onClick={() => { setStockByService(null); setMsg("Stock By Service cleared ✅"); }}
                style={{ padding: "6px 10px", fontSize: 12, borderRadius: 6, border: "1px solid #bbb", background: "#f5f5f5", cursor: "pointer" }}
              >
                Clear
              </button>
            </div>

            {stockByService && (
              <div style={{ marginTop: 12 }}>
                <h4>Stock By Service (Summary)</h4>
                <div className="table-wrap">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Service</th>
                        <th>Total Qty</th>
                        <th>Estimated Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Array.isArray(stockByService.summary) && stockByService.summary.map((s) => (
                        <tr key={`${s.serviceId}_${s.serviceName}`}>
                          <td>{s.serviceName}</td>
                          <td>{formatNumber(s.totalQty, 0)}</td>
                          <td>{formatNumber(s.totalValue, 2)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan={2} style={{ fontWeight: "bold", textAlign: "right" }}>Total:</td>
                        <td style={{ fontWeight: "bold" }}>{formatNumber(stockByService.grandTotal, 2)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>

                <h4 style={{ marginTop: 12 }}>Detailed Items</h4>
                <div className="table-wrap">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>AWB</th>
                        <th>Goods Description</th>
                        <th>Departure Date</th>
                        <th>Arrival Date</th>
                        <th>Delivery Date</th>
                        <th>Current Qty</th>
                        <th>Gross Mass</th>
                        <th>Days</th>
                        <th>Service</th>
                        <th>Unit Price</th>
                        <th>Estimated Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Array.isArray(stockByService.items) && stockByService.items.map((it) => (
                        <tr key={it.bol_reference}>
                          <td>{it.bol_reference}</td>
                          <td>{it.goods_description}</td>
                          <td>{it.date_of_departure}</td>
                          <td>{it.date_of_arrival}</td>
                          <td>{it.delivery_date || ""}</td>
                          <td>{formatNumber(it.current_qty, 0)}</td>
                          <td>{formatNumber(it.gross_mass, 2)}</td>
                          <td>{it.days}</td>
                          <td>{it.service ? it.service.name : "Unassigned"}</td>
                          <td>{formatNumber(it.unitPrice, 2)}</td>
                          <td>{formatNumber(it.value, 2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            
            {stockSummary && (
              <div className="grid grid-auto" style={{ marginTop: 12 }}>
                <div className="field">
                  <label>Current Qty (Total)</label>
                  <input readOnly value={formatNumber(stockSummary.current_total ?? 0, 0)} />
                </div>
                <div className="field">
                  <label>Total Added</label>
                  <input readOnly value={formatNumber(stockSummary.total_added ?? 0, 0)} />
                </div>
                <div className="field">
                  <label>Total Withdrawn</label>
                  <input readOnly value={formatNumber(stockSummary.total_withdrawn ?? 0, 0)} />
                </div>
                <div className="field">
                  <label>Withdrawn (From/To)</label>
                  <input readOnly value={formatNumber(stockSummary.withdrawn_in_range ?? 0, 0)} />
                </div>
              </div>
            )}
          </div>

          {/* Section 3: Stock Movements */}
          <div className="card" style={{ marginTop: 12 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
              <h4 style={{ margin: 0 }}>Stock Movements</h4>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input
                  value={manifestFilterId}
                  onChange={(e) => setManifestFilterId(e.target.value)}
                  placeholder="Manifest ID"
                  style={{ padding: "6px 8px", borderRadius: 6, border: "1px solid #ccc", width: 120 }}
                />
                <button type="button" onClick={loadStockMoves} disabled={loading}>
                  {loading ? "..." : "Load Movements"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setStockMoves([]);
                    setMsg("Movements cleared ✅");
                  }}
                  style={{
                    padding: "6px 10px",
                    fontSize: 12,
                    borderRadius: 6,
                    border: "1px solid #bbb",
                    background: "#f5f5f5",
                    cursor: "pointer",
                  }}
                >
                  Clear
                </button>
              </div>
            </div>

            {stockMoves.length > 0 && (
              <>
                <div style={{ marginTop: "20px" }}>
                  <h4>Stock In (Additions)</h4>
                  {inMoves.length > 0 ? (
                    <div className="table-wrap">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>AWB</th>
                            <th>Qty</th>
                            <th>Note</th>
                          </tr>
                        </thead>
                        <tbody>
                          {inMoves.map((m) => (
                            <tr key={m.id}>
                              <td>{m.created_at ? String(m.created_at).slice(0, 19).replace("T", " ") : ""}</td>
                              <td>{m.bol_reference}</td>
                              <td>{formatNumber(m.qty, 0)}</td>
                              <td>{m.note || ""}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="hint-text">No IN movements found for the selected filters.</p>
                  )}
                </div>

                <div style={{ marginTop: "20px" }}>
                  <h4>Stock Out (Withdrawals)</h4>
                  {outMoves.length > 0 ? (
                    <div className="table-wrap">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>AWB</th>
                            <th>Qty</th>
                            <th>Note</th>
                          </tr>
                        </thead>
                        <tbody>
                          {outMoves.map((m) => (
                            <tr key={m.id}>
                              <td>{m.created_at ? String(m.created_at).slice(0, 19).replace("T", " ") : ""}</td>
                              <td>{m.bol_reference}</td>
                              <td>{formatNumber(m.qty, 0)}</td>
                              <td>{m.note || ""}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="hint-text">No OUT movements found for the selected filters.</p>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ===== Tab: Financial ===== */}
      {tab === "financial" && (
        <div className="card">
          <h3>Stock Value Report (Detailed)</h3>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
            <button type="button" onClick={loadFinancialReport} disabled={loading}>
              {loading ? "..." : "Load Financial Report"}
            </button>
            <button
              type="button"
              onClick={() => {
                setFinancialReport(null);
                setMsg("Report cleared ✅");
              }}
              style={{
                padding: "6px 10px",
                fontSize: 12,
                borderRadius: 6,
                border: "1px solid #bbb",
                background: "#f5f5f5",
                cursor: "pointer",
              }}
            >
              Clear
            </button>
          </div>

          {financialReport && financialReport.report && (
            <div>
              <div style={{ marginBottom: 15, fontSize: "1.1rem", fontWeight: "bold", textAlign: "right" }}>
                Grand Total: {formatNumber(financialReport.grand_total, 2)}
              </div>

              {financialReport.report.map((op) => (
                <div key={op.operator} className="card" style={{ marginBottom: 20, border: "1px solid #e0e0e0" }}>
                  <div style={{ backgroundColor: "#f5f5f5", padding: "10px", fontWeight: "bold", fontSize: "1rem", borderBottom: "1px solid #ddd" }}>
                    Operator: {op.operator} <span style={{ float: "right" }}>Total: {formatNumber(op.total_value, 2)}</span>
                  </div>

                  {op.manifests.map((m) => (
                    <div key={m.id} style={{ padding: "10px", borderBottom: "1px dashed #eee" }}>
                      <div style={{ marginBottom: 8, fontSize: "0.9rem", color: "#555" }}>
                        <strong>Manifest:</strong> {m.id} &nbsp;|&nbsp; <strong>Voyage:</strong> {m.voyage_number} &nbsp;|&nbsp; <strong>Arrival:</strong> {String(m.date_of_arrival).slice(0, 10)} &nbsp;|&nbsp; <strong>Manifest Total:</strong> {formatNumber(m.total_value, 2)}
                      </div>
                      <div className="table-wrap">
                        <table className="data-table" style={{ fontSize: "0.85rem" }}>
                          <thead>
                            <tr>
                              <th>AWB No.</th>
                              <th>Arrival Date</th>
                              <th>Calc. Date (Today)</th>
                              <th>Goods Description</th>
                              <th>Weight</th>
                              <th>Days</th>
                              <th>Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            {m.bols.map((b) => (
                              <tr key={b.id}>
                                <td>{b.bol_reference}</td>
                                <td>{String(m.date_of_arrival).slice(0, 10)}</td>
                                <td>{new Date().toISOString().slice(0, 10)}</td>
                                <td>{b.goods_description}</td>
                                <td>{formatNumber(b.gross_mass, 2)}</td>
                                <td>{b.storage_days}</td>
                                <td>{formatNumber(b.value, 2)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ===== Tab: Delivered ===== */}
      {tab === "delivered" && (
        <div className="card">
          <h3>Delivered Stock Report (Detailed)</h3>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
            <button type="button" onClick={loadDeliveredReport} disabled={loading}>
              {loading ? "..." : "Load Delivered Report"}
            </button>
            <button type="button" onClick={loadDeliveredDetailed} disabled={loading}>
              {loading ? "..." : "Load Detailed Delivered Report"}
            </button>
            <button
              type="button"
              onClick={() => {
                setDeliveredReport(null);
                setDeliveredDetailed(null);
                setMsg("Report cleared ✅");
              }}
              style={{
                padding: "6px 10px",
                fontSize: 12,
                borderRadius: 6,
                border: "1px solid #bbb",
                background: "#f5f5f5",
                cursor: "pointer",
              }}
            >
              Clear
            </button>
          </div>

          {deliveredReport && deliveredReport.report && (
            <div>
              <div style={{ marginBottom: 15, fontSize: "1.1rem", fontWeight: "bold", textAlign: "right" }}>
                Grand Total: {formatNumber(deliveredReport.grand_total, 2)}
              </div>

              {deliveredReport.report.map((op) => (
                <div key={op.operator} className="card" style={{ marginBottom: 20, border: "1px solid #e0e0e0" }}>
                  <div style={{ backgroundColor: "#f5f5f5", padding: "10px", fontWeight: "bold", fontSize: "1rem", borderBottom: "1px solid #ddd" }}>
                    Operator: {op.operator} <span style={{ float: "right" }}>Total: {formatNumber(op.total_revenue, 2)}</span>
                  </div>

                  {op.manifests.map((m) => (
                    <div key={m.id} style={{ padding: "10px", borderBottom: "1px dashed #eee" }}>
                      <div style={{ marginBottom: 8, fontSize: "0.9rem", color: "#555" }}>
                        <strong>Manifest:</strong> {m.id} &nbsp;|&nbsp; <strong>Voyage:</strong> {m.voyage_number} &nbsp;|&nbsp; <strong>Arrival:</strong> {String(m.date_of_arrival).slice(0, 10)} &nbsp;|&nbsp; <strong>Manifest Total:</strong> {formatNumber(m.total_revenue, 2)}
                      </div>
                      <div className="table-wrap">
                        <table className="data-table" style={{ fontSize: "0.85rem" }}>
                          <thead>
                            <tr>
                              <th>AWB No.</th>
                              <th>Delivery Date</th>
                              <th>Goods Description</th>
                              <th>Weight</th>
                              <th>Day Subtracted?</th>
                              <th>Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            {m.bols.map((b) => (
                              <tr key={b.id}>
                                <td>{b.bol_reference}</td>
                                <td>{String(b.delivery_date).slice(0, 10)}</td>
                                <td>{b.goods_description}</td>
                                <td>{formatNumber(b.gross_mass, 2)}</td>
                                <td>{b.day_subtracted ? "Yes" : "No"}</td>
                                <td>{formatNumber(b.revenue, 2)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}

          {deliveredDetailed && (
            <div style={{ marginTop: 20 }}>
              <h4>Detailed Delivered BOLs</h4>
              <div className="table-wrap">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>AWB</th>
                      <th>Goods Description</th>
                      <th>Departure Date</th>
                      <th>Arrival Date</th>
                      <th>Delivery Date</th>
                      <th>Qty</th>
                      <th>Gross Mass</th>
                      <th>Days</th>
                      <th>Service</th>
                      <th>Unit Price</th>
                      <th>Estimated Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deliveredDetailed.map((row, idx) => (
                      <tr key={idx}>
                        <td>{row.bol_reference}</td>
                        <td>{row.goods_description}</td>
                        <td>{row.date_of_departure}</td>
                        <td>{row.date_of_arrival}</td>
                        <td>{row.delivery_date}</td>
                        <td>{formatNumber(row.qty, 0)}</td>
                        <td>{formatNumber(row.gross_mass, 2)}</td>
                        <td>{row.actual_days}</td>
                        <td>{row.service_name}</td>
                        <td>{formatNumber(row.unit_price, 2)}</td>
                        <td>{formatNumber(row.revenue, 2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ===== Tab: Reprint ===== */}
      {tab === "reprint" && (
        <div className="card">
          <h3>Re-print Documents</h3>
          <div className="grid grid-auto">
            <div className="field" style={{ gridColumn: "1 / -1" }}>
              <button type="button" onClick={handleReprintSearch} disabled={loading}>
                {loading ? "Searching..." : "Search AWB"}
              </button>
            </div>
          </div>

          {reprintResult && (
            <div style={{ marginTop: '1rem', borderTop: '1px solid #eee', paddingTop: '1rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {Array.isArray(reprintResult) ? (
                reprintResult.map((item, idx) => {
                  const { bol, header } = item;
                  const isDirectService = isDirectServiceItem(item);
                  const isDelivered = Number(bol.effective_is_delivered ?? bol.is_delivered) === 1;
                  const canReprintDO = isDirectService || Number(bol.can_reprint_do ?? bol.is_do_issued) === 1;
                  const canReprintInvoice = !isDirectService && Number(bol.can_reprint_invoice ?? bol.is_delivered) === 1;
                  const canReprintPOD = isDirectService || Number(bol.can_reprint_pod ?? bol.is_delivered) === 1;
                  const statusText = isDelivered ? "Delivered ✅" : canReprintDO ? "DO Issued 📜" : "Pending ⏳";
                  return (
                    <div className="card" key={bol.id || idx}>
                      <h4 style={{ margin: "0 0 0.5rem 0" }}>AWB: {bol.bol_reference}</h4>
                      <div className="grid grid-auto small-gap" style={{ fontSize: "0.9rem" }}>
                        <div><strong>Flight:</strong> {header.flight_no || header.voyage_number}</div>
                        <div><strong>Arrival:</strong> {header.date_of_arrival ? String(header.date_of_arrival).slice(0, 10) : ""}</div>
                        <div><strong>Consignee:</strong> {bol.consignee_name}</div>
                        <div><strong>Status:</strong> {statusText}</div>
                        {isDelivered && <div><strong>Delivery Date:</strong> {formatDateToYMD_forReprint(bol.delivery_date)}</div>}
                      </div>
                      
                      <div style={{ marginTop: '1rem', display: 'flex', gap: '10px', borderTop: '1px solid #eee', paddingTop: '1rem', flexWrap: 'wrap' }}>
                        {canReprintDO && (
                          <button type="button" onClick={() => handleReprintDO(item)}>
                            Re-print DO & Receipt
                          </button>
                        )}
                        {canReprintInvoice && (
                          <>
                            <button type="button" onClick={() => handleReprintInvoice(item)}>
                              Re-print Invoice
                            </button>
                          </>
                        )}
                        {canReprintPOD && (
                          <>
                            <button
                              type="button"
                              onClick={() => {
                                setPodReprintModalData(item);
                                setPodReprintModalCondition(SHIPMENT_CONDITION_GOOD);
                              }}
                            >
                              Re-print POD
                            </button>
                          </>
                        )}
                        {!canReprintDO && !canReprintInvoice && !canReprintPOD && (
                          <p className="hint-text">No documents available for re-printing for this AWB.</p>
                        )}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="message error">Error loading data.</div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ===== Tab: IRP ===== */}
      {tab === "irp" && (
        <div className="card">
          <h3>IRP Report</h3>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
            <button type="button" onClick={loadIrpUndelivered} disabled={loading}>
              {loading ? "..." : "Load Undelivered Shipments"}
            </button>
            <button
              type="button"
              onClick={() =>
                generateIrpReportPdf({
                  title: "Undelivered Shipments",
                  operator: irpOperator,
                  rows: irpUndelivered,
                })
              }
              disabled={loading || !irpUndelivered.length}
            >
              Print Undelivered
            </button>
            <button type="button" onClick={loadIrpDelivered} disabled={loading}>
              {loading ? "..." : "Load Delivered Shipments"}
            </button>
            <button
              type="button"
              onClick={() =>
                generateIrpReportPdf({
                  title: "Delivered Shipments",
                  operator: irpOperator,
                  rows: irpDelivered,
                })
              }
              disabled={loading || !irpDelivered.length}
            >
              Print Delivered
            </button>
            <button
              type="button"
              onClick={() => {
                setIrpUndelivered([]);
                setIrpDelivered([]);
                setMsg("IRP reports cleared ✅");
              }}
              style={{
                padding: "6px 10px",
                fontSize: 12,
                borderRadius: 6,
                border: "1px solid #bbb",
                background: "#f5f5f5",
                cursor: "pointer",
              }}
            >
              Clear
            </button>
          </div>

          <div className="card" style={{ marginTop: 12 }}>
            <h4 style={{ marginTop: 0 }}>Undelivered Shipments ({irpUndelivered.length})</h4>
            <div className="table-wrap">
              <table className="data-table" style={{ fontSize: "0.85rem" }}>
                <thead>
                  <tr>
                    <th>FLT NO / DATE</th>
                    <th>MAWB NO</th>
                    <th>HAWB NO</th>
                    <th>ORIGIN</th>
                    <th>PC(S)</th>
                    <th>WT (KGS)</th>
                    <th>COMMODITY</th>
                    <th>DATE AWB</th>
                    <th>REMARKS</th>
                    <th>DELIVERED</th>
                  </tr>
                </thead>
                <tbody>
                  {irpUndelivered.map((row, idx) => (
                    <tr key={`${row.bol_reference || "AWB"}-${idx}`}>
                      <td>{`${row.flight_no || ""} / ${row.flight_date || ""}`}</td>
                      <td>{row.mawb_no}</td>
                      <td>{row.hawb_no}</td>
                      <td>{row.origin_code}</td>
                      <td>{formatNumber(row.pcs, 0)}</td>
                      <td>{formatNumber(row.wt, 3)}</td>
                      <td>{row.commodity}</td>
                      <td>{row.awb_date}</td>
                      <td>{row.remarks}</td>
                      <td>{row.delivered}</td>
                    </tr>
                  ))}
                  {irpUndelivered.length === 0 && (
                    <tr>
                      <td colSpan={10} style={{ textAlign: "center" }}>No data loaded.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="card" style={{ marginTop: 12 }}>
            <h4 style={{ marginTop: 0 }}>Delivered Shipments ({irpDelivered.length})</h4>
            <div className="table-wrap">
              <table className="data-table" style={{ fontSize: "0.85rem" }}>
                <thead>
                  <tr>
                    <th>FLT NO / DATE</th>
                    <th>MAWB NO</th>
                    <th>HAWB NO</th>
                    <th>ORIGIN</th>
                    <th>PC(S)</th>
                    <th>WT (KGS)</th>
                    <th>COMMODITY</th>
                    <th>DATE AWB</th>
                    <th>REMARKS</th>
                    <th>DELIVERED</th>
                  </tr>
                </thead>
                <tbody>
                  {irpDelivered.map((row, idx) => (
                    <tr key={`${row.bol_reference || "AWB"}-${idx}`}>
                      <td>{`${row.flight_no || ""} / ${row.flight_date || ""}`}</td>
                      <td>{row.mawb_no}</td>
                      <td>{row.hawb_no}</td>
                      <td>{row.origin_code}</td>
                      <td>{formatNumber(row.pcs, 0)}</td>
                      <td>{formatNumber(row.wt, 3)}</td>
                      <td>{row.commodity}</td>
                      <td>{row.awb_date}</td>
                      <td>{row.remarks}</td>
                      <td>{row.delivered}</td>
                    </tr>
                  ))}
                  {irpDelivered.length === 0 && (
                    <tr>
                      <td colSpan={10} style={{ textAlign: "center" }}>No data loaded.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
      
       {/* ===== ✅ Tab: POD Reprint ===== */}
      {tab === "pod-reprint" && (
        <div className="card">
          <h3>POD Reprint by ID</h3>
          
          {/* قائمة منسدلة قابلة للفلترة */}
          <div className="grid grid-auto small-gap" style={{ alignItems: "end" }}>
            <div className="field" style={{ minWidth: 300 }}>
              <label>Select POD ID</label>
              <select
                value={selectedPodId}
                onChange={(e) => {
                  setSelectedPodId(e.target.value);
                  setPodDetails(null);
                }}
                style={{ width: "100%", padding: "8px", borderRadius: 6, border: "1px solid #ccc" }}
              >
                <option value="">-- Select POD --</option>
                {filteredPodList.map((pod) => (
                  <option key={pod.id} value={pod.id}>
                    POD #{pod.id} | AWB: {pod.bol_reference} | {pod.consignee_name} | {pod.created_at ? String(pod.created_at).slice(0, 10) : ""}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="field">
              <button 
                type="button" 
                onClick={handleLoadPodDetails} 
                disabled={loading || !selectedPodId}
                style={{ padding: "10px 20px" }}
              >
                {loading ? "Loading..." : "Load POD Details"}
              </button>
            </div>
          </div>

          {/* عرض تفاصيل POD */}
          {podDetails && (
            <div className="card" style={{ marginTop: 20, border: "2px solid #4caf50", background: "#f1f8e9" }}>
              <h4 style={{ color: "#2e7d32", marginTop: 0 }}>POD Details</h4>
              <div className="grid grid-auto small-gap" style={{ fontSize: "0.9rem" }}>
                <div><strong>POD ID:</strong> {podDetails.id}</div>
                <div><strong>AWB:</strong> {podDetails.bol_reference}</div>
                <div><strong>Consignee:</strong> {podDetails.bol_consignee || podDetails.consignee_name}</div>
                <div><strong>Voyage:</strong> {podDetails.voyage_number}</div>
                <div><strong>DO Received:</strong> {podDetails.do_received_date ? String(podDetails.do_received_date).slice(0, 10) : "-"}</div>
                <div><strong>DO Recipient:</strong> {podDetails.do_recipient || "-"}</div>
                <div><strong>Cargo Received:</strong> {podDetails.cargo_received_date ? String(podDetails.cargo_received_date).slice(0, 10) : "-"}</div>
                <div><strong>Cargo Recipient:</strong> {podDetails.cargo_recipient || "-"}</div>
                <div><strong>Packages:</strong> {podDetails.number_of_packages}</div>
                <div><strong>Weight:</strong> {podDetails.gross_mass}</div>
              </div>
              
              <div style={{ marginTop: 15 }}>
                <div className="field" style={{ marginBottom: 10, maxWidth: 420 }}>
                  <label>Shipment Condition (for POD print only)</label>
                  <select
                    value={podReprintShipmentCondition}
                    onChange={(e) => setPodReprintShipmentCondition(e.target.value)}
                  >
                    <option value={SHIPMENT_CONDITION_GOOD}>{SHIPMENT_CONDITION_GOOD}</option>
                    <option value={SHIPMENT_CONDITION_BAD}>{SHIPMENT_CONDITION_BAD}</option>
                  </select>
                </div>
                <button 
                  type="button" 
                  onClick={handlePrintPodById}
                  disabled={loading}
                  style={{ 
                    backgroundColor: "#4caf50", 
                    color: "white", 
                    padding: "12px 24px",
                    fontSize: "1rem",
                    fontWeight: "bold"
                  }}
                >
                  🖨️ Print POD
                </button>
              </div>
            </div>
          )}

          {/* عرض قائمة POD كجدول */}
          {filteredPodList.length > 0 && (
            <div style={{ marginTop: 20 }}>
              <h4>All PODs ({filteredPodList.length})</h4>
              <div className="table-wrap">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>POD ID</th>
                      <th>AWB</th>
                      <th>Consignee</th>
                      <th>Created</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPodList.map((pod) => (
                      <tr key={pod.id}>
                        <td>{pod.id}</td>
                        <td>{pod.bol_reference}</td>
                        <td>{pod.consignee_name}</td>
                        <td>{pod.created_at ? String(pod.created_at).slice(0, 10) : ""}</td>
                        <td>
                          <button 
                            type="button" 
                            onClick={() => {
                              setSelectedPodId(String(pod.id));
                              handleLoadPodDetails();
                            }}
                            disabled={loading}
                          >
                            Load & Print
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {doModalData && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0, 0, 0, 0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 3000,
            padding: "1rem",
          }}
          onClick={closeDoMetadataModal}
        >
          <div
            className="card"
            style={{ width: "min(680px, 100%)", maxHeight: "90vh", overflowY: "auto" }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginTop: 0 }}>Enter DO Data Before Re-print</h3>
            <div className="grid grid-auto small-gap">
              <div className="field">
                <label>DO Recipient *</label>
                <input
                  type="text"
                  value={doModalForm.doRecipient}
                  onChange={(e) => setDoModalForm((prev) => ({ ...prev, doRecipient: e.target.value }))}
                />
              </div>
              <div className="field">
                <label>Phone *</label>
                <input
                  type="text"
                  value={doModalForm.doRecipientPhone}
                  onChange={(e) => setDoModalForm((prev) => ({ ...prev, doRecipientPhone: e.target.value }))}
                />
              </div>
              <div className="field">
                <label>Received Date *</label>
                <input
                  type="date"
                  value={doModalForm.doReceivedDate}
                  onChange={(e) => setDoModalForm((prev) => ({ ...prev, doReceivedDate: e.target.value }))}
                />
              </div>
            </div>

            <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
              <button type="button" onClick={handleSaveDoMetadataAndPrint} disabled={doModalSaving}>
                {doModalSaving ? "Saving..." : "Save & Print DO + Receipt"}
              </button>
              <button type="button" onClick={closeDoMetadataModal} disabled={doModalSaving}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {podModalData && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0, 0, 0, 0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 3000,
            padding: "1rem",
          }}
          onClick={closePodModal}
        >
          <div
            className="card"
            style={{ width: "min(760px, 100%)", maxHeight: "90vh", overflowY: "auto" }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginTop: 0 }}>Create POD Before Print</h3>
            <div style={{ marginBottom: 10, opacity: 0.9 }}>
              AWB: <strong>{podModalData?.bol?.bol_reference}</strong>
            </div>

            <div className="grid grid-auto small-gap">
              <div className="field" style={{ display: 'none' }}>
                <label>DO Received Date *</label>
                <input
                  type="date"
                  value={podModalForm.doReceivedDate}
                  onChange={(e) => setPodModalForm((prev) => ({ ...prev, doReceivedDate: e.target.value }))}
                />
              </div>
              <div className="field">
                <label>DO Recipient *</label>
                <input
                  type="text"
                  value={podModalForm.doRecipient}
                  onChange={(e) => setPodModalForm((prev) => ({ ...prev, doRecipient: e.target.value }))}
                />
              </div>
              <div className="field">
                <label>DO Recipient Phone</label>
                <input
                  type="text"
                  value={podModalForm.doRecipientPhone}
                  onChange={(e) => setPodModalForm((prev) => ({ ...prev, doRecipientPhone: e.target.value }))}
                />
              </div>
              <div className="field">
                <label>Cargo Received Date</label>
                <input
                  type="date"
                  value={podModalForm.cargoReceivedDate}
                  onChange={(e) => setPodModalForm((prev) => ({ ...prev, cargoReceivedDate: e.target.value }))}
                />
              </div>
              <div className="field">
                <label>Cargo Recipient</label>
                <input
                  type="text"
                  value={podModalForm.cargoRecipient}
                  onChange={(e) => setPodModalForm((prev) => ({ ...prev, cargoRecipient: e.target.value }))}
                />
              </div>
              <div className="field" style={{ gridColumn: "1 / -1" }}>
                <label>Shipment Condition (for POD print only)</label>
                <select
                  value={podModalForm.shipmentCondition}
                  onChange={(e) => setPodModalForm((prev) => ({ ...prev, shipmentCondition: e.target.value }))}
                >
                  <option value={SHIPMENT_CONDITION_GOOD}>{SHIPMENT_CONDITION_GOOD}</option>
                  <option value={SHIPMENT_CONDITION_BAD}>{SHIPMENT_CONDITION_BAD}</option>
                </select>
              </div>
            </div>

            <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
              <button type="button" onClick={handleCreatePodAndPrint} disabled={podModalSaving}>
                {podModalSaving ? "Saving..." : "Save & Print POD"}
              </button>
              <button type="button" onClick={closePodModal} disabled={podModalSaving}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {podReprintModalData && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0, 0, 0, 0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 3000,
            padding: "1rem",
          }}
          onClick={() => setPodReprintModalData(null)}
        >
          <div
            className="card"
            style={{ width: "min(520px, 100%)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginTop: 0 }}>Select Shipment Condition</h3>
            <div style={{ marginBottom: 10, opacity: 0.9 }}>
              AWB: <strong>{podReprintModalData?.bol?.bol_reference}</strong>
            </div>

            <div className="field">
              <label>Shipment Condition</label>
              <select
                value={podReprintModalCondition}
                onChange={(e) => setPodReprintModalCondition(e.target.value)}
              >
                <option value={SHIPMENT_CONDITION_GOOD}>{SHIPMENT_CONDITION_GOOD}</option>
                <option value={SHIPMENT_CONDITION_BAD}>{SHIPMENT_CONDITION_BAD}</option>
              </select>
            </div>

            <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
              <button
                type="button"
                onClick={async () => {
                  const item = podReprintModalData;
                  setPodReprintModalData(null);
                  await handleReprintPOD(item, podReprintModalCondition);
                }}
                disabled={loading}
              >
                {loading ? "Printing..." : "Print POD"}
              </button>
              <button type="button" onClick={() => setPodReprintModalData(null)} disabled={loading}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
  


